import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight, Ruler } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { DrawingLegend, SectionDrawing } from '@/components/landing/section-drawing'
import { USE_CASES } from '@/constants/content'

export const UseCasesSection: FC = (): ReactNode => {
    return (
        <section
            id='einsatzbereiche'
            aria-labelledby='einsatzbereiche-title'
            className='py-24 sm:py-28'
        >
            <div className='mx-auto max-w-6xl px-6'>
                <SectionHeading
                    titleId='einsatzbereiche-title'
                    eyebrow='Einsatzbereiche'
                    title='Wo Temperaturmonitoring den Unterschied macht.'
                    description='Überall dort, wo Hydratationswärme zu kritischen Temperaturdifferenzen zwischen Kern und Rand führen kann.'
                />

                <ul className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                    {USE_CASES.map((useCase) => (
                        <li
                            key={useCase.title}
                            className='reveal flex flex-col overflow-hidden rounded-[1.75rem] border border-border bg-card'
                        >
                            <div className='border-b border-border bg-background/70 px-6 pb-4 pt-6'>
                                <SectionDrawing variant={useCase.drawing} />
                            </div>

                            <div className='flex flex-1 flex-col p-6'>
                                <h3 className='text-lg font-medium text-foreground'>
                                    {useCase.title}
                                </h3>

                                <p className='mt-2 text-pretty text-[0.95rem] leading-relaxed text-muted-foreground'>
                                    {useCase.text}
                                </p>
                            </div>
                        </li>
                    ))}

                    <li className='reveal flex flex-col justify-between rounded-[1.75rem] bg-primary p-7 text-primary-foreground'>
                        <div>
                            <span className='flex size-12 items-center justify-center rounded-2xl bg-white/10'>
                                <Ruler className='size-5' aria-hidden='true' />
                            </span>

                            <p className='mt-8 font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-primary-foreground/70'>
                                Faustregel
                            </p>

                            <h3 className='mt-2 text-balance text-2xl font-light leading-snug'>
                                Ab etwa 80 cm Bauteildicke wird Monitoring in der Regel relevant.
                            </h3>

                            <p className='mt-3 text-pretty leading-relaxed text-primary-foreground/80'>
                                Auch Zemente mit hoher Wärmeentwicklung oder extreme Hitze und Frost sprechen für eine Messung.
                            </p>
                        </div>

                        <Link
                            href='/#kontakt'
                            className='group mt-8 inline-flex h-11 w-fit items-center gap-2 rounded-full bg-card px-5 text-[0.95rem] font-medium text-foreground transition-colors hover:bg-background'
                        >
                            Bauteil einschätzen lassen
                            <ArrowRight className='size-4 text-primary transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </Link>
                    </li>
                </ul>

                <div className='mt-8'>
                    <DrawingLegend />
                </div>
            </div>
        </section>
    )
}
